import { useToastStore } from "@/stores/toast.store";
import { useMutation } from "@vue/apollo-composable";
import { graphql as gql } from "@/generated";
import { useI18n } from "vue-i18n";

const ModerateTextWithTokenDocument = gql(/* GraphQL */ `
  mutation ModerateTextWithToken($text: String!) {
    moderateTextWithToken(text: $text) {
      has_profanity
      analysis
      reason
      confirmation_token
    }
  }
`);

export function useTextModerationConfirmation() {
  const toastStore = useToastStore();
  const { t } = useI18n();
  const { mutate: moderateTextWithToken } = useMutation(
    ModerateTextWithTokenDocument,
    {
      fetchPolicy: "no-cache",
    },
  );

  async function getModerationConfirmation(
    message: string,
    retriesLeft = 5,
  ): Promise<string | null> {
    const result = await moderateTextWithToken({
      text: message,
    });

    const data = result?.data?.moderateTextWithToken;

    if (!result || result.errors || !data) {
      if (retriesLeft && retriesLeft > 0) {
        return getModerationConfirmation(message, retriesLeft - 1);
      }

      if (result?.errors) {
        toastStore.addToast({
          message: t("ui.moderation.unableToContactServices"),
          type: "error",
        });
      }

      return null;
    }

    if (data.has_profanity || !data.confirmation_token) {
      if (data.reason) {
        toastStore.addToast(
          {
            title: t("ui.moderation.messageBlocked"),
            message: data.reason,
            type: "error",
            position: "chat",
          },
          5000,
        );
      } else {
        toastStore.addToast(
          {
            message: t("ui.moderation.messageNotApproved"),
            type: "error",
          },
          5000,
        );
      }

      return null;
    }

    return data.confirmation_token;
  }

  return { getModerationConfirmation };
}
